import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api.ts";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { useToast } from "../../context/ToastContext";
import countries from "../../json/CountryFlagCode.json";

export default function EditProfile() {
  const { isAuthenticated, user, logout } = useAuth();
  const { language, translate } = useLanguage();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const isRTL = language === "ar";
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Helper function to convert ISO date to yyyy-MM-dd format
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toISOString().split("T")[0];
    } catch (error) {
      return "";
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    identificationNo: "",
    idExprDate: "",
    bankName: "",
    accountHolder: "",
    accountNumber: "",
    iban: "",
    businessName: "",
    businessRegNo: "",
  });

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [profileData, setProfileData] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("AE");

  // Load profile data on mount and when user changes
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadProfileData();
    }
    // eslint-disable-next-line
  }, [isAuthenticated, user?.id]);

  const handleDeleteAccount = async () => {
    if (!user?.id) {
      showError("User ID not found");
      return;
    }

    setShowDeleteConfirm(false);
    setIsDeleting(true);
    try {
      const response = await api.delete(`/users/buyer/account/${user.id}`);

      if (response.status === 200 || response.status === 204) {
        // Clear session storage
        sessionStorage.clear();

        // Clear local storage (removes token and all user data)
        localStorage.clear();

        // Show success message
        showSuccess("Account deleted successfully");

        // Redirect to login page using window.location to ensure it works
        setTimeout(() => {
          window.location.href = "/login";
        }, 500);
      } else {
        showError("Failed to delete account");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      showError(
        error.response?.data?.message ||
          "Failed to delete account. Please try again."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const loadProfileData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/users/user/${user.id}`);
      let profileData =
        response.data?.profile || response.data?.user || response.data;

      if (profileData) {
        // Derive local phone by stripping dial code (so code isn't shown twice)
        const rawMobile = (profileData.mobileNo || "").toString();
        let detectedCode = "AE"; // Default fallback
        let localPhone = rawMobile;
        try {
          if (rawMobile.startsWith("+")) {
            // Sort by dial code length (longest first) to match longest prefix first
            // e.g., +1649 should match Turks and Caicos (+1649) not Canada (+1)
            const sortedCountries = [...countries].sort(
              (a, b) => b.dial_code.length - a.dial_code.length
            );
            const match = sortedCountries.find((c) =>
              rawMobile.startsWith(c.dial_code)
            );
            if (match?.code && match?.dial_code) {
              detectedCode = match.code;
              localPhone = rawMobile.slice(match.dial_code.length);
            }
          }
        } catch {}
        setFormData({
          name:
            profileData.name ||
            profileData.fullName ||
            profileData.firstName ||
            "",
          email: profileData.email || "",
          phone: (localPhone || "").trim(),
          identificationNo: profileData.identificationNo || "",
          idExprDate: formatDateForInput(profileData.idExprDate) || "",
          bankName: profileData.seller?.bankName || profileData.bankName || "",
          accountHolder:
            profileData.seller?.accountHolderName ||
            profileData.seller?.accountHolder ||
            profileData.accountHolder ||
            profileData.accountHolderName ||
            "",
          accountNumber:
            profileData.seller?.accountNumber ||
            profileData.accountNumber ||
            "",
          iban: profileData.seller?.iban || profileData.iban || "",
          businessName:
            profileData.buyer?.businessName ||
            profileData.seller?.businessName ||
            profileData.businessName ||
            "",
          businessRegNo:
            profileData.buyer?.businessRegNo ||
            profileData.seller?.businessRegNo ||
            profileData.businessRegNo ||
            "",
        });
        // Set flag to detected country code (already detected above)
        setSelectedCountry(detectedCode);
        setProfileData(profileData);
      } else {
        showError(translate("editProfile.messages.failedToLoadProfile"));
      }
    } catch (error) {
      showError(translate("editProfile.messages.failedToLoadProfile"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim())
      newErrors.name = translate("editProfile.validation.nameRequired");

    if (!formData.phone.trim())
      newErrors.phone = translate("editProfile.validation.phoneRequired");
    if (!formData.identificationNo.trim())
      newErrors.identificationNo = translate(
        "editProfile.validation.eidRequired"
      );
    if (!formData.idExprDate)
      newErrors.idExprDate = translate(
        "editProfile.validation.eidExpiryRequired"
      );
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(translate("editProfile.messages.fixErrorsBeforeSaving"));
      return;
    }
    setIsLoading(true);
    try {
      // Get dial code from selected country and prepend to phone number
      const selectedCountryData = countries.find(
        (c) => c.code === selectedCountry
      );
      const dialCode = selectedCountryData?.dial_code;
      const fullMobileNo = `${dialCode}${formData.phone.trim()}`;

      let payload;

      if (user?.buyer?.id) {
        // Buyer payload
        payload = {
          name: formData.name,
          nickName: formData.nickName,
          mobileNo: fullMobileNo,
          identificationNo: formData.identificationNo,
          idExprDate: formData.idExprDate,
          buyer: {
            businessName: formData.businessName,
            businessRegNo: formData.businessRegNo,
          },
        };
      } else {
        // Seller payload
        payload = {
          name: formData.name,
          mobileNo: fullMobileNo,
          identificationNo: formData.identificationNo,
          idExprDate: formData.idExprDate,
          bankName: formData.bankName,
          accountHolderName: formData.accountHolder,
          accountNumber: formData.accountNumber,
          iban: formData.iban,
          businessName: formData.businessName,
          businessRegNo: formData.businessRegNo,
        };
      }

      const response = await api.put(`/users/edit/${user.id}`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.data.success
      ) {
        showSuccess(
          translate("editProfile.messages.profileUpdatedSuccessfully")
        );
        setIsEditing(false);
        loadProfileData();
      } else {
        showError(
          response.data.message ||
            translate("editProfile.messages.failedToUpdateProfile")
        );
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      showError(
        error.response?.data?.message ||
          translate("editProfile.messages.failedToUpdateProfile")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    loadProfileData();
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  if (!isAuthenticated) {
    return (
      <div className='flex justify-center items-center h-64'>
        <div className='text-center'>
          <div className='text-red-500 text-xl font-semibold mb-4'>
            {translate("editProfile.authenticationRequired")}
          </div>
          <p className='text-gray-600'>
            {translate("editProfile.pleaseLogin")}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading && !isEditing) {
    return (
      <div className='flex justify-center items-center h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#F15A29]'></div>
      </div>
    );
  }

  return (
    <div className='bg-gray-50' dir={isRTL ? "rtl" : "ltr"} lang={language}>
      {/* Personal Details */}
      <div className='rounded-[16px] mb-6 bg-white overflow-hidden'>
        <div className='bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-[16px] px-1 py-1 border-b border-[#cccccc]'>
          <div className='flex justify-between items-center p-[20px] text-lg rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F]   pb-2'>
            <h2 className='text-base sm:text-lg font-semibold'>
              {translate("editProfile.personalDetails.heading")}
            </h2>
            <div>{user.clientId}</div>
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 p-4'>
          {/* Name */}
          <div>
            <label className='block text-gray-600 mb-1 text-sm font-medium'>
              {translate("editProfile.personalDetails.fields.name")}{" "}
              <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              name='name'
              value={formData.name}
              onChange={handleInputChange}
              disabled={!isEditing}
              placeholder={translate(
                "editProfile.personalDetails.fields.namePlaceholder"
              )}
              className={`w-full border rounded-[12px] p-3 placeholder:text-sm text-gray-600 ${
                errors.name ? "border-red-500" : "border-[#949494]"
              } ${!isEditing ? "bg-gray-100" : ""}`}
            />
            {errors.name && (
              <p className='text-red-500 text-xs mt-1'>{errors.name}</p>
            )}
          </div>

          {/* Email (read-only) */}
          <div>
            <label className='block text-gray-600 mb-1 text-sm font-medium'>
              {translate("editProfile.personalDetails.fields.email")}
            </label>
            <input
              type='email'
              name='email'
              value={formData.email}
              readOnly
              disabled
              placeholder={translate(
                "editProfile.personalDetails.fields.emailPlaceholder"
              )}
              className={`w-full border rounded-[12px] p-3 placeholder:text-sm text-gray-600 bg-gray-100 border-[#949494]`}
            />
          </div>

          {/* Phone, EID, and EID Expiry in one row - spanning full width */}
          <div className='col-span-1 md:col-span-2'>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              {/* Phone */}
              <div>
                <label className='block text-gray-600 mb-1 text-sm font-medium'>
                  {translate("editProfile.personalDetails.fields.phone")}{" "}
                  <span className='text-red-500'>*</span>
                </label>
                <div className='flex'>
                  <div className='flex flex-row w-full h-12 border border-[#13131399] rounded-lg bg-white overflow-hidden focus-within:ring-orange-500' style={{ direction: 'ltr' }}>
                    {/* Country Code Select with Flag - Always on left */}
                    <div className='relative flex-shrink-0' style={{ minWidth: '80px' }}>
                      <div className='absolute left-2 top-1/2 transform -translate-y-1/2 w-10 h-5 z-10'>
                        <img
                          src={`https://flagcdn.com/w40/${selectedCountry.toLowerCase()}.png`}
                          alt='flag'
                          className='w-full h-full object-cover rounded-sm'
                        />
                      </div>
                      <select
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        disabled={!isEditing}
                        className='h-full pl-12 pr-2 bg-white focus:outline-none appearance-none cursor-pointer rounded-l-lg text-left'
                        style={{ direction: 'ltr' }}
                      >
                        {countries
                          .sort((a, b) =>
                            a.dial_code.localeCompare(b.dial_code)
                          )
                          .map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.dial_code}
                            </option>
                          ))}
                      </select>
                    </div>

                    {/* Phone Number Input */}
                    <input
                      type='tel'
                      name='phone'
                      value={formData.phone || ''}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder='1234'
                      maxLength='15'
                      className={`flex-1 px-4 h-full bg-white focus:outline-none rounded-r-lg ${
                        errors.phone ? "border-red-500" : ""
                      } ${!isEditing ? "bg-gray-100" : ""}`}
                      style={{ minWidth: 0, direction: isRTL ? 'rtl' : 'ltr' }}
                      dir={isRTL ? "rtl" : "ltr"}
                    />
                  </div>
                </div>
                {errors.phone && (
                  <p className='text-red-500 text-xs mt-1'>{errors.phone}</p>
                )}
              </div>

              {/* EID */}
              <div>
                <label className='block text-gray-600 mb-1 text-sm font-medium'>
                  {translate("editProfile.personalDetails.fields.eid")}{" "}
                  <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  name='identificationNo'
                  value={formData.identificationNo}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder={translate(
                    "editProfile.personalDetails.fields.eidPlaceholder"
                  )}
                  className={`w-full border rounded-[12px] p-3 placeholder:text-sm text-gray-600 ${
                    errors.identificationNo
                      ? "border-red-500"
                      : "border-[#949494]"
                  } ${!isEditing ? "bg-gray-100" : ""}`}
                />
                {errors.identificationNo && (
                  <p className='text-red-500 text-xs mt-1'>
                    {errors.identificationNo}
                  </p>
                )}
              </div>

              {/* EID Expiry */}
              <div>
                <label className='block text-gray-600 mb-1 text-sm font-medium'>
                  {translate("editProfile.personalDetails.fields.eidExpiry")}{" "}
                  <span className='text-red-500'>*</span>
                </label>
                <input
                  type='date'
                  name='idExprDate'
                  value={formatDateForInput(formData.idExprDate)}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full border rounded-[12px] p-3 text-gray-600 ${
                    errors.idExprDate ? "border-red-500" : "border-[#949494]"
                  } ${!isEditing ? "bg-gray-100" : ""}`}
                />
                {errors.idExprDate && (
                  <p className='text-red-500 text-xs mt-1'>
                    {errors.idExprDate}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bank Details - Only show for Corporate buyers or Sellers */}
      {(!user?.buyer?.id || profileData?.buyer?.buyerTypeId === 2) && (
        <div className='rounded-[16px] mb-6 bg-white overflow-hidden'>
          <div className='bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-[16px] px-1 py-1 border-b border-[#cccccc]'>
            <div className='p-[20px] text-lg rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2'>
              <h2 className='text-base sm:text-lg font-semibold'>
                Business Details
              </h2>
            </div>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 p-4'>
            {/* Check if user is a buyer or seller */}
            {user?.buyer?.id ? (
              /* Buyer-specific logic: Show bank details only for Corporate/Business buyers */
              <>
                {/* Business Name */}
                <div>
                  <label className='block text-gray-600 mb-1 text-sm font-medium'>
                    Business Name
                  </label>
                  <input
                    type='text'
                    name='businessName'
                    value={formData.businessName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder='Enter business name'
                    className={`w-full border rounded-[12px] p-3 placeholder:text-sm text-gray-600 ${
                      errors.businessName
                        ? "border-red-500"
                        : "border-[#949494]"
                    } ${!isEditing ? "bg-gray-100" : ""}`}
                  />
                  {errors.businessName && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.businessName}
                    </p>
                  )}
                </div>

                {/* Business Registration Number */}
                <div>
                  <label className='block text-gray-600 mb-1 text-sm font-medium'>
                    Business Registration Number
                  </label>
                  <input
                    type='text'
                    name='businessRegNo'
                    value={formData.businessRegNo}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder='Enter business registration number'
                    className={`w-full border rounded-[12px] p-3 placeholder:text-sm text-gray-600 ${
                      errors.businessRegNo
                        ? "border-red-500"
                        : "border-[#949494]"
                    } ${!isEditing ? "bg-gray-100" : ""}`}
                  />
                  {errors.businessRegNo && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.businessRegNo}
                    </p>
                  )}
                </div>
              </>
            ) : (
              /* Seller: Show all original bank details fields */
              <>
                {/* Bank Name */}
                <div>
                  <label className='block text-gray-600 mb-1 text-sm font-medium'>
                    {translate("editProfile.bankDetails.fields.bankName")}
                  </label>
                  <input
                    type='text'
                    name='bankName'
                    value={formData.bankName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder={translate(
                      "editProfile.bankDetails.fields.bankNamePlaceholder"
                    )}
                    className={`w-full border rounded-[12px] p-3 placeholder:text-sm text-gray-600 ${
                      errors.bankName ? "border-red-500" : "border-[#949494]"
                    } ${!isEditing ? "bg-gray-100" : ""}`}
                  />
                  {errors.bankName && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.bankName}
                    </p>
                  )}
                </div>

                {/* Account Holder */}
                <div>
                  <label className='block text-gray-600 mb-1 text-sm font-medium'>
                    {translate("editProfile.bankDetails.fields.accountHolder")}
                  </label>
                  <input
                    type='text'
                    name='accountHolder'
                    value={formData.accountHolder}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder={translate(
                      "editProfile.bankDetails.fields.accountHolderPlaceholder"
                    )}
                    className={`w-full border rounded-[12px] p-3 placeholder:text-sm text-gray-600 ${
                      errors.accountHolder
                        ? "border-red-500"
                        : "border-[#949494]"
                    } ${!isEditing ? "bg-gray-100" : ""}`}
                  />
                  {errors.accountHolder && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.accountHolder}
                    </p>
                  )}
                </div>

                {/* Account Number */}
                <div>
                  <label className='block text-gray-600 mb-1 text-sm font-medium'>
                    {translate("editProfile.bankDetails.fields.accountNumber")}
                  </label>
                  <input
                    type='text'
                    name='accountNumber'
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder={translate(
                      "editProfile.bankDetails.fields.accountNumberPlaceholder"
                    )}
                    className={`w-full border rounded-[12px] p-3 placeholder:text-sm text-gray-600 ${
                      errors.accountNumber
                        ? "border-red-500"
                        : "border-[#949494]"
                    } ${!isEditing ? "bg-gray-100" : ""}`}
                  />
                  {errors.accountNumber && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.accountNumber}
                    </p>
                  )}
                </div>

                {/* IBAN */}
                <div>
                  <label className='block text-gray-600 mb-1 text-sm font-medium'>
                    {translate("editProfile.bankDetails.fields.iban")}
                  </label>
                  <input
                    type='text'
                    name='iban'
                    value={formData.iban}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder={translate(
                      "editProfile.bankDetails.fields.ibanPlaceholder"
                    )}
                    className={`w-full border rounded-[12px] p-3 placeholder:text-sm text-gray-600 ${
                      errors.iban ? "border-red-500" : "border-[#949494]"
                    } ${!isEditing ? "bg-gray-100" : ""}`}
                  />
                  {errors.iban && (
                    <p className='text-red-500 text-xs mt-1'>{errors.iban}</p>
                  )}
                </div>

                {/* Business Name - Only show for Corporate sellers */}
                {profileData?.seller?.sellerType?.name === "Corporate" && (
                  <div>
                    <label className='block text-gray-600 mb-1 text-sm font-medium'>
                      Business Name
                    </label>
                    <input
                      type='text'
                      name='businessName'
                      value={formData.businessName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder='Enter business name'
                      className={`w-full border rounded-[12px] p-3 placeholder:text-sm text-gray-600 ${
                        errors.businessName
                          ? "border-red-500"
                          : "border-[#949494]"
                      } ${!isEditing ? "bg-gray-100" : ""}`}
                    />
                    {errors.businessName && (
                      <p className='text-red-500 text-xs mt-1'>
                        {errors.businessName}
                      </p>
                    )}
                  </div>
                )}

                {/* Business Registration Number - Only show for Corporate sellers */}
                {profileData?.seller?.sellerType?.name === "Corporate" && (
                  <div>
                    <label className='block text-gray-600 mb-1 text-sm font-medium'>
                      Business Registration Number
                    </label>
                    <input
                      type='text'
                      name='businessRegNo'
                      value={formData.businessRegNo}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder='Enter business registration number'
                      className={`w-full border rounded-[12px] p-3 placeholder:text-sm text-gray-600 ${
                        errors.businessRegNo
                          ? "border-red-500"
                          : "border-[#949494]"
                      } ${!isEditing ? "bg-gray-100" : ""}`}
                    />
                    {errors.businessRegNo && (
                      <p className='text-red-500 text-xs mt-1'>
                        {errors.businessRegNo}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className='flex justify-between items-center gap-4'>
        {/* Delete Account Button */}
        <button
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isDeleting || isLoading}
          className='px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-50'
        >
          {isDeleting ? "Deleting..." : "Delete Account"}
        </button>

        {/* Edit/Save/Cancel Buttons */}
        <div className='flex justify-end gap-4'>
          {!isEditing ? (
            <button
              onClick={handleEdit}
              className='px-8 py-3 bg-[#F15A29] text-white rounded-lg hover:bg-[#d14a1f] transition font-medium'
            >
              {translate("editProfile.buttons.editProfile")}
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className='px-8 py-3 border border-gray-400 text-gray-600 rounded-lg hover:bg-gray-50 transition font-medium disabled:opacity-50'
              >
                {translate("editProfile.buttons.cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className='px-8 py-3 bg-[#F15A29] text-white rounded-lg hover:bg-[#d14a1f] transition font-medium disabled:opacity-50 flex items-center gap-2'
              >
                {isLoading ? (
                  <>
                    <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                    {translate("editProfile.buttons.saving")}
                  </>
                ) : (
                  translate("editProfile.buttons.saveChanges")
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Show loading when deleting */}
      {isDeleting && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 max-w-md w-full mx-4 text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4'></div>
            <p className='text-gray-700'>Deleting your account...</p>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className='fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4'>
          <div className='bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden'>
            <div className='px-6 py-4 border-b border-gray-200 bg-[#F153291F]'>
              <h3 className='text-lg font-semibold text-gray-800'>
                Confirm account deletion
              </h3>
            </div>
            <div className='px-6 py-5 space-y-3 text-gray-700'>
              <p className='font-medium'>Are you sure?</p>
              <p className='text-sm text-gray-600'>
                This will permanently delete your account and cannot be undone.
              </p>
            </div>
            <div className='px-6 py-4 bg-gray-50 flex justify-end gap-3'>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className='px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white transition font-medium disabled:opacity-50'
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-50'
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
